import React, { useState } from 'react';
import {
  Button,
  Typography,
  TextField,
  Dialog,
  DialogContent,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { ISubTopic } from '../../../../../store/SubTopicSlice';
import { generateStructuredEssayQuestion } from '../../../../../store/StructuredEssayQuestionSlice';

export interface IGenerateQuestionModal {
  open: boolean;
  onClose: () => void;
  subTopicObj?: ISubTopic;
}

const GenerateQuestionModal: React.FC<IGenerateQuestionModal> = ({
  open,
  onClose,
  subTopicObj,
}) => {
  const topicObj = subTopicObj?.topic;
  // State for form fields
  const [curriculum, setCurriculum] = useState(topicObj?.curriculum || '');
  const [grade, setGrade] = useState(topicObj?.grade || '');
  const [subject, setSubject] = useState(topicObj?.subject || '');
  const [topic, setTopic] = useState(topicObj?.name || '');
  const [subTopic, setSubTopic] = useState(subTopicObj?.name || '');
  const [details, setDetails] = useState('');
  const [numberOfQuestions, setNumberOfQuestions] = useState('1');
  const [selectedFile, setSelectedFile] = useState(null);

  const dispatch = useDispatch();

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleGenerate = async () => {
    const prompt = `You are an expert educational AI assistant designed to generate structured essay questions and corresponding marking schemes in JSON format based on provided curriculum and academic context.
    Schema is:
    {
        questions: Array<{
            mainQuestion: string;
            difficulty: 'EASY' | 'MEDIUM' | 'HARD';
            subQuestions: Array<{
                sortingOrder: number;
                question: string;
                marks: string;
                markingScheme: string;
            }>;
            totalMarks: number;
        }>;
    }
    
    ## Instructions:
    1. Based on the information below, generate **${numberOfQuestions} structured essay questions**.
    2. For each question:
    - Provide a **clear, grade-appropriate structured essay questions** based on the subtopic.
    - Ensure the question assesses **critical thinking**, **concept understanding**, and **application**.
    - Use correct terminology and align with typical exam formats of the given curriculum.

    3. Each main question, should devide into sub questions. Each sub guestion should have seperate marks and marking scheme:
    - Provide a **detailed marking scheme**.
    - Clearly explain what is expected in a full-mark response.
    - Use bullet points or a numbered list to make the marking scheme structured and easy to read.

    4. Use Markdown formatting:
    - Total marks per question should be realistic (typically 6–12 marks).
    - Marks for the sub-questions should be as follows,
      1st sub-question - 1 or 2 marks
      2nd sub-question - 3 or 4 marks
      3rd sub-question - 4 or 5 marks
    - Keep everything structured, easy to follow, and exam-ready.

    ## Provided Details:
    - Curriculum: ${curriculum}
    - Grade: ${grade}
    - Subject: ${subject}
    - Topic: ${topic}
    - Subtopic: ${subTopic}
    ${details ? `- Additional details: ${details}` : ''}
    Do not return any explanations or commentary — only the clean JSON. Answers should be clear, correct, and well-structured.
    Ensure that questions and marking schemes are tailored for students at the ${grade} level within the ${curriculum} curriculum and reflect the difficulty appropriate for that level.`;

    const formData = new FormData();
    formData.append('message', prompt);
    if (selectedFile !== null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      formData.append('file', selectedFile);
    }

    dispatch(generateStructuredEssayQuestion(formData));
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: '10px',
          borderRadius: '20px',
          maxHeight: '700px',
          width: '550px',
        },
      }}
    >
      <DialogContent>
        <Typography
          component="h2"
          sx={{
            fontWeight: 'bold',
            fontSize: '1.5rem',
            textAlign: 'center',
          }}
        >
          Generate Questions with AI
        </Typography>

        <TextField
          fullWidth
          label="Curriculum"
          value={curriculum}
          onChange={(e) => setCurriculum(e.target.value)}
          margin="normal"
          sx={{ mt: 4 }}
        />
        <TextField
          fullWidth
          label="Grade"
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="SubTopic"
          value={subTopic}
          onChange={(e) => setSubTopic(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Resource"
          type="file"
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handleFileChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Additional Details"
          placeholder="Additional Details"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Number of Questions"
          placeholder="Number of Questions"
          value={numberOfQuestions}
          onChange={(e) => setNumberOfQuestions(e.target.value)}
          margin="normal"
        />

        <Button
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={{ mt: 2 }}
        >
          Dismiss
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerate}
          sx={{ mt: 2, ml: 2, float: 'inline-end' }}
        >
          Generate
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateQuestionModal;
